import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Activity,
  Battery,
  Box,
  CalendarClock,
  CheckCircle2,
  Hash,
  Lock,
  Pencil,
  Power,
  PowerOff,
  Save,
  Trash2,
  Wifi,
  X,
} from 'lucide-react';
import {
  Buton,
  Etiket,
  Girdi,
  Iskelet,
  Kart,
  KartBaslik,
  KartIcerik,
  OnayDialog,
  Rozet,
  toast,
  type RozetTonu,
} from '@emanetx/ui';
import {
  dolapGuncelleIstegi,
  type DolapBoyutu,
  type DolapDurumu,
  type DolapGuncelleIstegi,
} from '@emanetx/shared-types';
import type { Dolap } from '@emanetx/sdk';
import { OlaylarTablosu } from '../cihazlar/olaylar-tablosu.js';
import { silmeHatasiniFormatla } from '../silme-hata.js';
import { useDolap, useDolapGuncelle, useDolapSil, useDolapZorlaSifirla } from './api.js';
import { ZorlaSifirlaDialog } from './zorla-sifirla-dialog.js';

const DURUM_TONU: Record<DolapDurumu, RozetTonu> = {
  MUSAIT: 'yesil',
  MESGUL: 'turuncu',
  REZERVE: 'mavi',
  BAKIM: 'gri',
  CEVRIMDISI: 'kirmizi',
};

const DURUM_ETIKET: Record<DolapDurumu, string> = {
  MUSAIT: 'Müsait',
  MESGUL: 'Meşgul',
  REZERVE: 'Rezerve',
  BAKIM: 'Bakım',
  CEVRIMDISI: 'Çevrimdışı',
};

const BOYUT_ETIKET: Record<DolapBoyutu, string> = {
  KUCUK: 'Küçük (S)',
  ORTA: 'Orta (M)',
  BUYUK: 'Büyük (L)',
  EKSTRA: 'Ekstra (XL)',
};

export interface DolapDetayIcerikOzellikleri {
  dolapId: string;
  /** Silindikten sonra ne olsun — sayfada navigate, modal'da kapat. */
  onSilSonra: () => void;
}

export function DolapDetayIcerik({ dolapId, onSilSonra }: DolapDetayIcerikOzellikleri) {
  const sorgu = useDolap(dolapId);
  const guncelle = useDolapGuncelle(dolapId);
  const sil = useDolapSil();
  const [silOnay, setSilOnay] = useState(false);
  const [duzenle, setDuzenle] = useState(false);

  if (sorgu.isLoading) {
    return (
      <div className="space-y-4">
        <Iskelet className="h-10 w-1/3" />
        <Iskelet className="h-40" />
        <Iskelet className="h-40" />
      </div>
    );
  }

  if (sorgu.isError || !sorgu.data) {
    return (
      <div className="border-fume-200 dark:border-fume-800 dark:bg-fume-900 rounded-xl border bg-white p-6 text-center">
        <p className="text-fume-600 dark:text-fume-300 text-sm">Dolap bulunamadı.</p>
      </div>
    );
  }

  const d = sorgu.data;

  return (
    <div className="space-y-6">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <h2 className="text-2xl font-bold tracking-tight">
              <span className="text-fume-500">{d.noktaKod ?? '—'}</span>{' '}
              <span className="text-fume-400">/</span> Dolap{' '}
              <span className="font-mono">#{d.numara}</span>
            </h2>
            <Rozet ton={DURUM_TONU[d.durum]} nokta>
              {DURUM_ETIKET[d.durum]}
            </Rozet>
          </div>
          <p className="text-fume-500 mt-1 text-sm">{d.noktaAd ?? '—'}</p>
        </div>
        {duzenle ? (
          <Buton stil="sade" onClick={() => setDuzenle(false)}>
            <X size={14} /> İptal
          </Buton>
        ) : (
          <div className="flex flex-wrap gap-2">
            <HizliDurumDugmesi
              dolap={d}
              calisiyor={guncelle.isPending}
              onDegistir={async (yeniDurum) => {
                try {
                  await guncelle.mutateAsync({ durum: yeniDurum });
                  toast.success(yeniDurum === 'MUSAIT' ? 'Dolap aktif' : 'Dolap bakıma alındı');
                } catch (err) {
                  toast.error((err as Error).message);
                }
              }}
            />
            <Buton stil="cizgili" onClick={() => setDuzenle(true)}>
              <Pencil size={14} /> Düzenle
            </Buton>
            <Buton stil="tehlike" onClick={() => setSilOnay(true)}>
              <Trash2 size={14} /> Sil
            </Buton>
          </div>
        )}
      </header>

      {duzenle ? (
        <DuzenleFormu
          dolapId={d.id}
          baslangic={{
            numara: d.numara,
            boyut: d.boyut,
            durum: (['MUSAIT', 'BAKIM', 'CEVRIMDISI'] as const).includes(
              d.durum as 'MUSAIT' | 'BAKIM' | 'CEVRIMDISI',
            )
              ? (d.durum as 'MUSAIT' | 'BAKIM' | 'CEVRIMDISI')
              : 'MUSAIT',
            kiralamaSaati: d.kiralamaSaati,
            periyotUcreti: Number(d.periyotUcreti),
            saatlikUcret: Number(d.saatlikUcret),
            cezaUcreti: Number(d.cezaUcreti),
          }}
          durumKilitli={d.durum === 'MESGUL' || d.durum === 'REZERVE'}
          mevcutDurum={d.durum}
          calisiyor={guncelle.isPending}
          onKaydet={async (v) => {
            try {
              await guncelle.mutateAsync(v);
              toast.success('Dolap güncellendi');
              setDuzenle(false);
            } catch (err) {
              toast.error((err as Error).message);
            }
          }}
        />
      ) : (
        <CihazVeFiyatKartlari dolap={d} />
      )}

      <Kart>
        <KartBaslik>
          <h3 className="flex items-center gap-2 text-base font-semibold">
            <Power size={16} className="text-marka-500" /> Manuel komut
          </h3>
        </KartBaslik>
        <KartIcerik>
          <p className="text-fume-500 dark:text-fume-400 mb-4 text-sm">
            Cihaz bağlantısı kurulduktan sonra bu panelden komut göndereceğiz. Şu an arka uç hazır
            değil — Adım 4 ile devreye girecek.
          </p>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            <Buton stil="cizgili" disabled>
              🔓 Aç
            </Buton>
            <Buton stil="cizgili" disabled>
              🔒 Kilitle
            </Buton>
            <Buton stil="cizgili" disabled>
              💡 LED
            </Buton>
            <Buton stil="cizgili" disabled>
              📊 Durum
            </Buton>
          </div>
        </KartIcerik>
      </Kart>

      <Kart>
        <KartBaslik>
          <h3 className="flex items-center gap-2 text-base font-semibold">
            <Activity size={16} className="text-marka-500" /> Olaylar
          </h3>
        </KartBaslik>
        <KartIcerik>
          <OlaylarTablosu dolapId={d.id} kompakt />
        </KartIcerik>
      </Kart>

      <OnayDialog
        acik={silOnay}
        onDegisim={setSilOnay}
        baslik={`Dolap #${d.numara} silinsin mi?`}
        aciklama="Aktif kiralama veya geçmiş kayıt varsa silme engellenir; o durumda dolap durumunu BAKIM veya ÇEVRİMDIŞI yapabilirsiniz."
        onayMetni="Evet, sil"
        iptalMetni="Vazgeç"
        tur="tehlike"
        onOnay={async () => {
          try {
            await sil.mutateAsync(d.id);
            toast.success('Dolap silindi');
            onSilSonra();
          } catch (err) {
            toast.error(silmeHatasiniFormatla(err), { duration: 6000 });
          }
        }}
      />
    </div>
  );
}

function CihazVeFiyatKartlari({ dolap: d }: { dolap: Dolap }) {
  return (
    <div className="grid gap-4 lg:grid-cols-2">
      <Kart>
        <KartBaslik>
          <h3 className="text-base font-semibold">Cihaz</h3>
        </KartBaslik>
        <KartIcerik className="space-y-3">
          <Alan
            ikon={Hash}
            etiket="Cihaz UID"
            deger={d.cihazUid ?? <span className="text-fume-400">Henüz eşleşmedi</span>}
          />
          <Alan ikon={Box} etiket="Boyut" deger={BOYUT_ETIKET[d.boyut]} />
          <Alan ikon={Lock} etiket="Kapı durumu" deger={d.kapiDurumu} />
          <Alan
            ikon={Battery}
            etiket="Pil"
            deger={
              d.pilMv !== null && d.pilMv !== undefined ? (
                `${d.pilMv} mV`
              ) : (
                <span className="text-fume-400">—</span>
              )
            }
          />
          <Alan
            ikon={Wifi}
            etiket="RSSI"
            deger={
              d.rssi !== null && d.rssi !== undefined ? (
                `${d.rssi} dBm`
              ) : (
                <span className="text-fume-400">—</span>
              )
            }
          />
          <Alan
            ikon={CalendarClock}
            etiket="Son ping"
            deger={
              d.sonPing ? (
                new Date(d.sonPing).toLocaleString('tr-TR')
              ) : (
                <span className="text-fume-400">—</span>
              )
            }
          />
        </KartIcerik>
      </Kart>

      <Kart>
        <KartBaslik>
          <h3 className="text-base font-semibold">Fiyatlandırma (varsayılan)</h3>
        </KartBaslik>
        <KartIcerik className="space-y-3">
          <Alan ikon={CalendarClock} etiket="Periyot" deger={`${d.kiralamaSaati} saat`} />
          <Alan
            ikon={Hash}
            etiket="Periyot ücreti"
            deger={<span className="font-mono">{d.periyotUcreti}</span>}
          />
          <Alan
            ikon={Hash}
            etiket="Saatlik aşım"
            deger={<span className="font-mono">{d.saatlikUcret}</span>}
          />
          <Alan
            ikon={Hash}
            etiket="Ceza"
            deger={<span className="font-mono">{d.cezaUcreti}</span>}
          />
          <p className="text-fume-400 text-xs">
            Dinamik fiyat kuralları aktifse gerçek ücret kurallarla hesaplanır.
          </p>
        </KartIcerik>
      </Kart>
    </div>
  );
}

function HizliDurumDugmesi({
  dolap,
  calisiyor,
  onDegistir,
}: {
  dolap: Dolap;
  calisiyor: boolean;
  onDegistir: (yeniDurum: 'MUSAIT' | 'BAKIM') => Promise<void>;
}) {
  if (dolap.durum === 'MESGUL' || dolap.durum === 'REZERVE') {
    return (
      <Rozet ton="turuncu" nokta className="px-3 py-1.5">
        {dolap.durum === 'MESGUL' ? 'Kiralamada' : 'Rezerve'}
      </Rozet>
    );
  }
  if (dolap.durum === 'BAKIM') {
    return (
      <Buton stil="birincil" disabled={calisiyor} onClick={() => void onDegistir('MUSAIT')}>
        <CheckCircle2 size={14} /> Aktif Yap
      </Buton>
    );
  }
  // MUSAIT veya CEVRIMDISI
  return (
    <Buton stil="ikincil" disabled={calisiyor} onClick={() => void onDegistir('BAKIM')}>
      <PowerOff size={14} /> Bakıma Al
    </Buton>
  );
}

function DuzenleFormu({
  dolapId,
  baslangic,
  durumKilitli,
  mevcutDurum,
  calisiyor,
  onKaydet,
}: {
  dolapId: string;
  baslangic: DolapGuncelleIstegi;
  durumKilitli: boolean;
  mevcutDurum?: string;
  calisiyor: boolean;
  onKaydet: (v: DolapGuncelleIstegi) => Promise<void>;
}) {
  const [zorlaAcik, setZorlaAcik] = useState(false);
  const form = useForm<DolapGuncelleIstegi>({
    resolver: zodResolver(dolapGuncelleIstegi),
    defaultValues: baslangic,
  });

  useEffect(() => {
    form.reset(baslangic);
  }, [baslangic, form]);

  const gonder = form.handleSubmit(async (v) => {
    await onKaydet(v);
  });

  return (
    <form onSubmit={gonder}>
      <Kart>
        <KartBaslik>
          <h3 className="text-base font-semibold">Bilgileri düzenle</h3>
        </KartBaslik>
        <KartIcerik className="space-y-4">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Etiket htmlFor="numara">Numara</Etiket>
              <Girdi id="numara" {...form.register('numara')} />
              {form.formState.errors.numara && (
                <p className="text-xs text-red-600">{form.formState.errors.numara.message}</p>
              )}
            </div>
            <div className="space-y-1.5">
              <Etiket htmlFor="boyut">Boyut</Etiket>
              <select
                id="boyut"
                className="border-fume-200 focus:ring-marka-500 dark:border-fume-700 dark:bg-fume-900 dark:text-fume-50 h-11 w-full rounded-lg border bg-white px-3 text-sm focus:outline-none focus:ring-2"
                {...form.register('boyut')}
              >
                <option value="KUCUK">Küçük (S)</option>
                <option value="ORTA">Orta (M)</option>
                <option value="BUYUK">Büyük (L)</option>
                <option value="EKSTRA">Ekstra (XL)</option>
              </select>
            </div>
          </div>

          <div className="space-y-1.5">
            <Etiket htmlFor="durum">Durum</Etiket>
            <select
              id="durum"
              disabled={durumKilitli}
              className="border-fume-200 focus:ring-marka-500 dark:border-fume-700 dark:bg-fume-900 dark:text-fume-50 h-11 w-full rounded-lg border bg-white px-3 text-sm focus:outline-none focus:ring-2 disabled:opacity-50"
              {...form.register('durum')}
            >
              <option value="MUSAIT">Müsait — satışa açık</option>
              <option value="BAKIM">Bakım — satışa kapalı</option>
              <option value="CEVRIMDISI">Çevrimdışı — cihaz bekleniyor</option>
            </select>
            <div className="flex items-center justify-between gap-2 pt-1">
              {durumKilitli ? (
                <p className="text-fume-400 text-xs">
                  Aktif kiralama/rezervasyon var, normal durum değişikliği kilitli.
                </p>
              ) : (
                <p className="text-fume-400 text-xs">
                  Yukarıdan değiştirip Kaydet — veya alttan manuel zorla.
                </p>
              )}
              <button
                type="button"
                onClick={() => setZorlaAcik(true)}
                className="text-xs font-semibold text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300"
              >
                ⚙ Zorla Değiştir →
              </button>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
            <div className="space-y-1.5">
              <Etiket htmlFor="kiralamaSaati">Periyot (saat)</Etiket>
              <Girdi
                id="kiralamaSaati"
                type="number"
                min={1}
                {...form.register('kiralamaSaati', { valueAsNumber: true })}
              />
            </div>
            <div className="space-y-1.5">
              <Etiket htmlFor="periyotUcreti">Periyot ücreti</Etiket>
              <Girdi
                id="periyotUcreti"
                type="number"
                step="0.01"
                {...form.register('periyotUcreti', { valueAsNumber: true })}
              />
            </div>
            <div className="space-y-1.5">
              <Etiket htmlFor="saatlikUcret">Saatlik aşım</Etiket>
              <Girdi
                id="saatlikUcret"
                type="number"
                step="0.01"
                {...form.register('saatlikUcret', { valueAsNumber: true })}
              />
            </div>
            <div className="space-y-1.5">
              <Etiket htmlFor="cezaUcreti">Ceza</Etiket>
              <Girdi
                id="cezaUcreti"
                type="number"
                step="0.01"
                {...form.register('cezaUcreti', { valueAsNumber: true })}
              />
            </div>
          </div>
        </KartIcerik>
        <div className="border-fume-100 dark:border-fume-800 flex justify-end gap-2 border-t px-6 py-3">
          <Buton type="submit" disabled={calisiyor}>
            <Save size={14} /> {calisiyor ? 'Kaydediliyor...' : 'Kaydet'}
          </Buton>
        </div>
      </Kart>
      <ZorlaSifirlaDialog
        acik={zorlaAcik}
        onDegisim={setZorlaAcik}
        dolapId={dolapId}
        mevcutDurum={mevcutDurum}
      />
    </form>
  );
}

function Alan({
  ikon: Ikon,
  etiket,
  deger,
}: {
  ikon: React.ComponentType<{ size?: number; className?: string }>;
  etiket: string;
  deger: React.ReactNode;
}) {
  return (
    <div className="flex items-start gap-3">
      <Ikon size={16} className="text-fume-400 mt-0.5 flex-shrink-0" />
      <div className="min-w-0 flex-1">
        <p className="text-fume-500 text-xs">{etiket}</p>
        <div className="text-fume-800 dark:text-fume-100 break-words text-sm">{deger}</div>
      </div>
    </div>
  );
}
